import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient, type QueryKey } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Lock, Target } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card'
import { Skeleton } from '@/components/ui/skeleton'
import { useNetwork } from '@/contexts/NetworkContext'
import { segmentIdsIn, useSegmentNames } from '@/hooks/useSegmentNames'
import { apiErrorDescription } from '@/services/core/apiError'
import {
  countTargetingTypes,
  hiddenTargetingTypes,
  targetingType,
  typesInUse,
  type Targeting,
} from '@/services/targeting/targeting'
import { ChipRow } from '../ChipRow'
import { TargetingDialog } from './TargetingDialog'
import { summarizeTargeting } from './targetingSummary'

interface TargetingListCellProps<TObject extends { targeting: Targeting }> {
  /** The object's detail query — the one its edit page reads, so they share a cache. */
  queryKey: QueryKey
  queryFn: () => Promise<TObject>
  /**
   * The object's targeting as the list row carries it, when the list sent it
   * in full. Shown and edited without fetching the object.
   */
  targeting?: Targeting
  /** Saves new targeting on the object and returns it as saved. */
  save: (targeting: Targeting) => Promise<TObject>
  /** Why the targeting cannot be changed from the list. Leaves it read-only. */
  readOnlyReason?: string
}

/**
 * Targeting in a list row: hover to read it, click to edit it in the same
 * editor the detail page uses. A row from a concise list has no targeting of
 * its own, so then the full object is fetched once the cell is hovered or clicked.
 */
export function TargetingListCell<TObject extends { targeting: Targeting }>({
  queryKey,
  queryFn,
  targeting: listed,
  save,
  readOnlyReason,
}: TargetingListCellProps<TObject>) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { currentNetwork } = useNetwork()
  const [wanted, setWanted] = useState(false)
  const [editing, setEditing] = useState(false)

  const { data, isLoading } = useQuery({ queryKey, queryFn, enabled: wanted && !listed })
  // The object's own query wins when it holds anything: a save from here, or
  // the edit page, keeps it current while the list row may not have refetched.
  const targeting = data?.targeting ?? listed
  const { inTargeting } = useSegmentNames(segmentIdsIn(targeting ?? {}))
  const lines = targeting ? summarizeTargeting(inTargeting(targeting), t) : []

  const mutation = useMutation({
    mutationFn: save,
    onSuccess: (saved) => {
      queryClient.setQueryData(queryKey, saved)
      toast.success(t('common.saved'), { duration: 3000 })
    },
    onError: (error) => {
      toast.error(t('common.error'), { description: apiErrorDescription(error), duration: 5000 })
    },
  })

  // Whatever is already targeted stays offered, even a type the network hides.
  const inUse = new Set(typesInUse(targeting))
  const hidden = hiddenTargetingTypes(currentNetwork).filter((id) => !inUse.has(id))

  return (
    // The row navigates on click; nothing in here, the dialog included, should.
    <span onClick={(e) => e.stopPropagation()}>
      <HoverCard openDelay={200} onOpenChange={(open) => open && setWanted(true)}>
        <HoverCardTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="xs"
            loading={mutation.isPending}
            // Not disabled when read-only: a disabled button gets no hover, and
            // the targeting should still be readable.
            aria-label={readOnlyReason ? t('targeting.title') : t('targeting.editTargeting')}
            onClick={() => {
              setWanted(true)
              if (!readOnlyReason) setEditing(true)
            }}
          >
            {readOnlyReason ? <Lock /> : <Target />}
            {targeting
              ? countTargetingTypes(targeting) > 0
                ? t('targeting.typeCount', { count: countTargetingTypes(targeting) })
                : t('targeting.none')
              : t('targeting.title')}
          </Button>
        </HoverCardTrigger>
        <HoverCardContent align="start" className="w-96">
          {isLoading || !targeting ? (
            <Skeleton className="h-12 w-full" />
          ) : lines.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('targeting.none')}</p>
          ) : (
            <div className="divide-y divide-dashed">
              {lines.map((line) => (
                <ChipRow
                  key={line.id}
                  label={line.label}
                  icon={targetingType(line.id).icon}
                  chips={line.chips}
                  stacked
                  className="first:pt-0 last:pb-0"
                />
              ))}
            </div>
          )}
          {readOnlyReason && (
            <p className="mt-2 text-xs text-muted-foreground">{readOnlyReason}</p>
          )}
        </HoverCardContent>
      </HoverCard>
      {targeting && (
        <TargetingDialog
          open={editing}
          onOpenChange={setEditing}
          targeting={targeting}
          onApply={(next) => mutation.mutate(next)}
          hiddenTypes={hidden}
        />
      )}
    </span>
  )
}
